// at least one of these has to be in the query params to make a search
export const REQUIRED_CRITERIA = [
  'cameraOperatorId',
  'creator',
  'custodian',
  'externalId',
  'hasRework',
  'hasMissingCoverageData',
  'imageApid',
  'imageArk',
  'imageGroupNumbers',
  'keyword',
  'naturalGroupIds',
  'needsToBeOrganized',
  'place',
  'projectId',
  'startDateAvailable',
  'surname',
  'collectionIds',
]
const CRITERIA_TO_DELETE = ['fullMap']
export const MULTILINE_REGEX = /[,，\s]+/

export function validateSearchCriteria(criteria) {
  const requiredInCriteria = REQUIRED_CRITERIA.some((key) => Object.prototype.hasOwnProperty.call(criteria, key))
  return !(requiredInCriteria || criteria.jiapuOnly || criteria.volumeSetSearch)
}

export function adjustQueryParamsForSearch(queryParams) {
  const queryParamsCopy = { ...queryParams }

  // Remove Marketo tracking params
  delete queryParamsCopy.cid
  delete queryParamsCopy.mkt_tok

  // Trim all params
  Object.entries(queryParamsCopy).forEach(([key, value]) => {
    if (typeof value === 'string') {
      queryParamsCopy[key] = value.trim()
    }
  })

  delete queryParamsCopy.advanced
  if (queryParamsCopy.collectionIds) {
    queryParamsCopy.types = ['COLLECTIONS']
    queryParamsCopy.collectionIds = queryParamsCopy.collectionIds.split(',')
  }

  if (queryParamsCopy.groupIds) {
    queryParamsCopy.groupIds = queryParamsCopy.groupIds.split(MULTILINE_REGEX)
  }

  if (queryParamsCopy.showUnknown) {
    queryParamsCopy.showUnknown = queryParamsCopy.showUnknown === 'true'
  }

  if (queryParamsCopy.includeDeprecatedGroups) {
    queryParamsCopy.includeDeprecatedGroups = queryParamsCopy.includeDeprecatedGroups === 'true'
  }

  if (queryParamsCopy.hasRework) {
    queryParamsCopy.hasRework = queryParamsCopy.hasRework === 'true'
  }

  if (queryParamsCopy.hasSupervisorReviewActions) {
    queryParamsCopy.hasSupervisorReviewActions = queryParamsCopy.hasSupervisorReviewActions === 'true'
  }
  if (queryParamsCopy.hasMissingCoverageData) {
    queryParamsCopy.hasMissingCoverageData = queryParamsCopy.hasMissingCoverageData === 'true'
  }

  if (queryParamsCopy.imageGroupNumbers) {
    queryParamsCopy.imageGroupNumbers = queryParamsCopy.imageGroupNumbers.split(MULTILINE_REGEX)
  }

  if (queryParamsCopy.jiapuOnly) {
    queryParamsCopy.volumeSetSearch = 'jiapu'
    queryParamsCopy.types = ['VOLUME_SET']
  } else {
    delete queryParamsCopy.types
  }

  delete queryParamsCopy.jiapuOnly

  if (queryParamsCopy.lifeEvent) {
    queryParamsCopy.lifeEventIds = [Number(queryParamsCopy.lifeEvent)]
    delete queryParamsCopy.lifeEvent
  }

  if (queryParamsCopy.needsToBeOrganized) {
    queryParamsCopy.needsToBeOrganized = queryParamsCopy.needsToBeOrganized === 'true'
  }

  if (queryParamsCopy.page) {
    queryParamsCopy.page = Number(queryParamsCopy.page)
  }

  if (queryParamsCopy.pageSize) {
    queryParamsCopy.pageSize = Number(queryParamsCopy.pageSize)
  }

  if (queryParamsCopy.place) {
    queryParamsCopy.place = Number(queryParamsCopy.place)
  }

  if (queryParamsCopy.lifeEventRecordTypes) {
    if (typeof queryParamsCopy.lifeEventRecordTypes === 'string') {
      queryParamsCopy.lifeEventRecordTypeIds = queryParamsCopy.lifeEventRecordTypes.split(',').map((rt) => Number(rt))
    } else {
      queryParamsCopy.lifeEventRecordTypeIds = queryParamsCopy.lifeEventRecordTypes?.map((id) => Number(id))
    }
    delete queryParamsCopy.lifeEventRecordTypes
  }

  if (queryParamsCopy.recordTypes) {
    if (typeof queryParamsCopy.recordTypes === 'string') {
      queryParamsCopy.recordTypeIds = queryParamsCopy.recordTypes.split(',').map((rt) => Number(rt))
    }

    // If recordTypes is not a string, delete it anyway since it isn't valid
    delete queryParamsCopy.recordTypes
  }

  if (!['number', 'undefined'].includes(typeof queryParamsCopy.titleNumbers)) {
    queryParamsCopy.titleNumbers = [Number(queryParamsCopy.titleNumbers)]
  }

  if (queryParamsCopy.recordTypeIds) {
    if (typeof queryParamsCopy.recordTypeIds === 'string') {
      const arr = []
      const split = queryParamsCopy.recordTypeIds.split(',')

      split.forEach((element) => arr.push(parseInt(element, 10)))
      queryParamsCopy.recordTypeIds = arr
    } else if (typeof queryParamsCopy.recordTypeIds === 'object') {
      const arr = []
      queryParamsCopy.recordTypeIds.forEach((element) => arr.push(parseInt(element, 10)))
      queryParamsCopy.recordTypeIds = arr
    }
  }

  if (queryParamsCopy.volumeSetSearch) {
    if (['jiapu', 'jokbo', 'keizu'].includes(queryParamsCopy.volumeSetSearch)) {
      queryParamsCopy.types = ['VOLUME_SET']
    } else {
      delete queryParamsCopy.volumeSetSearch
    }
  }

  if (queryParamsCopy.surname) {
    queryParamsCopy.surnames = queryParamsCopy.surname.split(MULTILINE_REGEX)
    delete queryParamsCopy.surname
  } else {
    delete queryParamsCopy.surname
  }

  if (!queryParamsCopy.clan) {
    delete queryParamsCopy.clan
  }

  if (queryParamsCopy.sortBy) {
    if (queryParamsCopy.sortBy === 'dgsNumber') {
      queryParamsCopy.sortBy = {
        field: 'groupName',
        order: queryParamsCopy.sortOrder || 'ASC',
      }
      delete queryParamsCopy.sortOrder
    }
    if (queryParamsCopy.sortBy === 'dateCreated') {
      queryParamsCopy.sortBy = {
        field: 'created',
        order: queryParamsCopy.sortOrder || 'DESC',
      }
      delete queryParamsCopy.sortOrder
    }
  }

  CRITERIA_TO_DELETE.forEach((key) => {
    delete queryParamsCopy[key]
  })

  return queryParamsCopy
}

/**
 * Transform query params from the current product to be compatible with this page
 * @param params query params
 * @return paramsCopy new query params
 */
export function getBackwardsCompatibleParams(params) {
  const paramsCopy = { ...params }

  if (paramsCopy.cameraOperator) {
    paramsCopy.cameraOperatorId = paramsCopy.cameraOperator
    paramsCopy.cameraOperator = null
  }

  if (paramsCopy.apid) {
    paramsCopy.imageApid = paramsCopy.apid
    paramsCopy.apid = null
  }

  if (paramsCopy.ark) {
    paramsCopy.imageArk = paramsCopy.ark
    paramsCopy.ark = null
  }

  if (paramsCopy.showImageRework) {
    paramsCopy.hasRework = paramsCopy.showImageRework
    paramsCopy.showImageRework = null
  }

  if (paramsCopy.imageArk && paramsCopy.imageArk.startsWith('61903/')) {
    paramsCopy.imageArk = paramsCopy.imageArk.replace(/61903\//, '')
  }

  if (paramsCopy.dgsNumbers) {
    paramsCopy.imageGroupNumbers = paramsCopy.dgsNumbers
    paramsCopy.dgsNumbers = null
  }

  return paramsCopy
}

/* Shallow comparison of each property in each set of query params */
export function isEqualShallow(obj1, obj2) {
  if (!obj1 || !obj2) {
    return obj1 === obj2
  }
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  return (
    keys1.length === keys2.length &&
    keys1.every((key) => Object.prototype.hasOwnProperty.call(obj2, key) && obj1[key] === obj2[key])
  )
}

/* Remove keys with falsy values */
export function deleteFalsyValues(obj) {
  if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach((key) => {
      if (!obj[key] || obj[key] === 'false') {
        delete obj[key]
      }
    })
  }
}

// Currently, the primary and record type ids come as an object from LifeEventsInput
export function adjustLifeEventRecordTypes(input) {
  if (input?.lifeEventRecordTypes) {
    if (!input?.lifeEvent) {
      delete input.lifeEventRecordTypes
      return input
    }
    if (Array.isArray(input.lifeEventRecordTypes)) input.lifeEventRecordTypes = input.lifeEventRecordTypes.join(',')
    else if (typeof input.lifeEventRecordTypes !== 'string') {
      const primary = input.lifeEventRecordTypes.primary?.map((id) => Number(id)) || []
      const related = input.lifeEventRecordTypes.related?.map((id) => Number(id)) || []

      input.lifeEventRecordTypes = [...primary, ...related].join(',')
    }

    if (!input.lifeEventRecordTypes.length) {
      delete input.lifeEventRecordTypes
    }
  }

  return input
}

export function removeEmptyCriteria(searchCriteria) {
  return Object.entries(searchCriteria).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {})
}
